import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { EnumDropdown } from 'framework/form';
import {
  DropDownFilter,
  Filters,
  FiltersLayout,
  List,
  ListHeader,
  ListPageLayout,
  useListContext,
} from 'framework/list';
import { Loading } from 'framework/ui/mui-builder';
import { BusinessFunctionDropdown } from 'domain/shared/businessFunction';
import { POSITION_SLOT_WORKER_TYPE_LABELS } from 'domain/people/positionSlots/constants';
import SpecializationFilter from 'domain/people/positionSlots/filter/SpecializationFilter';
import PositionSlotOfficeFilter from 'domain/people/positionSlots/filter/PositionSlotOfficeFilter';
import type { PositionSlotListItem } from 'domain/people/positionSlots/types';
import useWorkforceAnalyticsService from 'domain/people/workforceAnalytics/useWorkforceAnalyticsService';
import {
  WORKFORCE_ANALYTICS_POSITION_SLOT_PAGE_SIZE,
  WorkforceAnalyticsViewBy,
} from 'domain/people/workforceAnalytics/constants';
import { buildWorkforceAnalyticsRows } from 'domain/people/workforceAnalytics/utils';
import WorkforceAnalyticsTable from 'domain/people/workforceAnalytics/List/components/WorkforceAnalyticsTable/WorkforceAnalyticsTable';
import WorkforceAnalyticsViewBySelect from 'domain/people/workforceAnalytics/List/components/WorkforceAnalyticsViewBySelect/WorkforceAnalyticsViewBySelect';

type WorkforceAnalyticsFiltersProps = {
  viewBy: WorkforceAnalyticsViewBy;
  onViewByChange: (value: WorkforceAnalyticsViewBy) => void;
};

const WorkforceAnalyticsFilters = ({ viewBy, onViewByChange }: WorkforceAnalyticsFiltersProps) => (
  <Filters>
    <Box sx={{ display: 'inline-flex', mr: 1.5, mb: 1.5 }}>
      <WorkforceAnalyticsViewBySelect value={viewBy} onChange={onViewByChange} />
    </Box>
    <FiltersLayout>
      <PositionSlotOfficeFilter source="specification:positionSlotByOfficeSpecification" label="Office" />
      <DropDownFilter source="workerType" label="Staff type">
        <EnumDropdown source="workerType" options={POSITION_SLOT_WORKER_TYPE_LABELS} />
      </DropDownFilter>
      <DropDownFilter source="businessFunctionId" label="Business functions">
        <BusinessFunctionDropdown source="businessFunctionId" label="Business functions" multiple />
      </DropDownFilter>
      <SpecializationFilter source="specializationId" label="Positions" />
    </FiltersLayout>
  </Filters>
);

const WorkforceAnalyticsContent = ({ viewBy }: { viewBy: WorkforceAnalyticsViewBy }) => {
  const { data: { items = [] } = {}, loading } = useListContext();
  const positionSlots = items as PositionSlotListItem[];
  const rows = useMemo(() => buildWorkforceAnalyticsRows(positionSlots, viewBy), [positionSlots, viewBy]);

  return (
    <Box sx={{ minHeight: '240px', position: 'relative' }}>
      {loading ? <Loading isInitialPosition /> : <WorkforceAnalyticsTable rows={rows} />}
    </Box>
  );
};

const WorkforceAnalyticsPage = () => {
  const { getWorkforceAnalyticsPositionSlotsListData } = useWorkforceAnalyticsService();
  const [viewBy, setViewBy] = useState(WorkforceAnalyticsViewBy.POSITION);

  return (
    <List
      getDataMethod={getWorkforceAnalyticsPositionSlotsListData}
      pageSize={WORKFORCE_ANALYTICS_POSITION_SLOT_PAGE_SIZE}
    >
      <ListPageLayout
        header={<ListHeader label="Position Analytics" />}
        filter={<WorkforceAnalyticsFilters viewBy={viewBy} onViewByChange={setViewBy} />}
        alwaysOpen
      >
        <WorkforceAnalyticsContent viewBy={viewBy} />
      </ListPageLayout>
    </List>
  );
};

export default WorkforceAnalyticsPage;
